import { Stack, useTheme, Typography } from '@mui/material';
import Logo from './Logo/Logo.svg';
import { ReactNode } from 'react';

// @svgr/webpack runs SVGO preset-default, whose removeViewBox plugin strips a viewBox that
// equals the SVG's own width/height attributes. Logo.svg carries
// viewBox="0 0 196 64" width="196" height="64", so the built bundle ships it with NO viewBox at
// all (Storybook and Vitest are unaffected: neither runs SVGO, which is why this only shows up
// in a real Next.js build). preserveAspectRatio has no user coordinate system to scale against
// without a viewBox, so any fixed CSS width/height on the rendered <svg> (e.g. the Mobile Nav
// band below) crops it instead of scaling it. Passing viewBox explicitly here restores the
// coordinate system. Mirrors CLARITY_LOGO_VIEW_BOXES in
// apps/customer-portal/src/components/ClarityLogo/ClarityLogo.tsx and ICON_MAPPINGS in
// packages/components/src/Logo/Logo.tsx.
//
// Keep this in sync with Logo.svg's own viewBox: Landing.test.tsx reads the file directly and
// asserts the rendered svg's viewBox matches it exactly.
export const LANDING_DEFAULT_LOGO_VIEW_BOX = '0 0 196 64';

export type LandingProps = {
  title: string;
  description: string;
  children: ReactNode;
  /**
   * Custom logo component to display in the landing page.
   * Defaults to the VerifiMe Logo if not provided.
   */
  logo?: ReactNode;
  /**
   * Opts this screen into the design system's Mobile Nav: below `md`, the hero collapses from
   * the 210px brand panel (a 1:3 flex ratio against `100vh`) to a 48px band carrying only the
   * logo. Defaults to `false` so every caller that does not pass it (five other customer-portal
   * screens plus admin-portal's login page) is unaffected. `md` and up is unaffected either way.
   */
  mobileNav?: boolean;
  /**
   * Forwarded to the hero `Stack` so a consumer (e.g. this component's own test) can locate and
   * measure the header band directly, without depending on the logo it happens to render.
   */
  'data-testid'?: string;
};

export default function Landing({
  title,
  description,
  children,
  logo = <Logo viewBox={LANDING_DEFAULT_LOGO_VIEW_BOX} />,
  mobileNav = false,
  'data-testid': dataTestId,
}: LandingProps) {
  const theme = useTheme();

  // Every responsive array below (`direction`, `flex`, `paddingY`, `display`) is keyed the
  // same way: index 0 for `xs`, index 1 for `sm`, index 2 for `md` and up. The breakpoint is
  // `md`, not `sm` (i.e. not this package's `useMobile()` hook, which checks `down('sm')`):
  // the outer `Stack` itself only switches from a column to a row at `md` (see `direction`
  // below), so the hero must compact at that same breakpoint. Checking `sm` instead would
  // leave the `sm` band stacked while still carrying the full desktop hero.
  return (
    // `minHeight`, not a fixed `height`: at `md` and up this still fills at least the
    // viewport, matching today's visual, but a fixed height caps the container. On the
    // stacked column layout, content taller than one viewport needs the container free to
    // grow so the page flows and scrolls naturally instead of overflowing a box that stopped
    // at 100vh (which would also leave white space under the purple hero once the hero itself
    // stops being exactly 100vh tall). See the `TallContent` story, which pins this for the
    // `md`-and-up case.
    <Stack direction={['column', 'column', 'row']} minHeight="100vh">
      <Stack
        // Deliberate exception to the root-only `data-testid` rule: MUI `Stack` renders a plain
        // `div` with no accessible role or name, so there is no semantic locator (`getByRole`,
        // `getByText`, ...) for this node the way there is for a button or heading. The
        // alternative is a structural lookup from the true root (e.g.
        // `svg.closest('.MuiStack-root')`, which this file's own `Compact` story used before
        // `mobileNav` existed) and that is exactly the fragility `mobileNav`'s conditional logo
        // wrapper (below) breaks: adding a wrapping `Stack` around the logo changes which
        // ancestor `.closest('.MuiStack-root')` resolves to. A stable testid on the node actually
        // being measured is the more reliable answer for a node with no semantics.
        data-testid={dataTestId}
        sx={(muiTheme) => ({ backgroundColor: muiTheme.palette.primary.main })}
        gap={theme.semantic.spacing.lg}
        // Mobile Nav's band is `padding: 8px 20px` (the board's own CSS, reproduced here from
        // `spacing.xs`/`spacing.lg` rather than copied as literals): a fixed padding at every
        // width below `md`, not the two-step [0, 3xl] the non-Mobile-Nav hero uses.
        paddingX={
          mobileNav
            ? [theme.semantic.spacing.lg, theme.semantic.spacing.lg, theme.semantic.spacing['3xl']]
            : [0, theme.semantic.spacing['3xl']]
        }
        paddingY={
          mobileNav
            ? [theme.semantic.spacing.xs, theme.semantic.spacing.xs, theme.semantic.spacing['4xl']]
            : [
                theme.semantic.spacing['2xs'],
                theme.semantic.spacing['2xs'],
                theme.semantic.spacing['4xl'],
              ]
        }
        // Below `md` the hero must not flex-grow to fill the column's share of `minHeight`
        // (100vh): with `flex: 1` it would stretch far past its own content, undoing the
        // padding reduction above. `0 0 auto` sizes it to its content (logo + padding) instead,
        // leaving the content Stack below to take the rest of the page (see its own `flex`
        // prop). Mobile Nav pins that content-sized band to an explicit `48px` (`8px` padding +
        // `32px` logo height either side) instead of leaving it to whatever the caller's logo
        // happens to measure.
        flex={
          mobileNav
            ? [
                `0 0 ${theme.semantic.spacing['3xl']}`,
                `0 0 ${theme.semantic.spacing['3xl']}`,
                1,
              ]
            : ['0 0 auto', '0 0 auto', 1]
        }
        justifyContent="center"
        alignItems="center"
        color={theme.palette.primary.contrastText}
      >
        {mobileNav ? (
          // Wrapping Stack exists only when `mobileNav` is set: the non-Mobile-Nav path below
          // renders `{logo}` completely unwrapped, exactly as it always has, so a caller that
          // does not opt in gets byte-for-byte the same DOM this component produced before this
          // prop existed (including what `svg.closest('.MuiStack-root')` finds in the `Compact`
          // story). TD-042: the board's Mobile Nav logo is 120x32 (3.75:1). No size token
          // expresses 120px (semantic.size steps 4xs at 64px, 3xs at 128px, nothing between).
          // The width is therefore a hardcoded CSS override on whatever logo node the caller
          // passes, not a token; see docs/technical-debt.md TD-042. Height alone comes from a
          // token (`size['6xs']`, 32px). The SVG's own `preserveAspectRatio` (default `xMidYMid
          // meet`) does the scaling, so an off-ratio logo (e.g. client-portal's default 196x64
          // `Logo.svg`, ~3.06:1) letterboxes inside the 120x32 box instead of distorting. This
          // only holds when the rendered `<svg>` actually carries a `viewBox`: without one,
          // `preserveAspectRatio` has no coordinate system to scale against and the fixed CSS
          // width/height above crops the logo instead. That is exactly what a real Next.js
          // build produces for the default `<Logo />` unless it is given an explicit `viewBox`
          // prop (see `LANDING_DEFAULT_LOGO_VIEW_BOX` above), because @svgr/webpack's SVGO
          // strips a viewBox that equals the SVG's own width/height. A caller-supplied `logo`
          // must carry the same guarantee itself (see `ClarityLogo`'s `CLARITY_LOGO_VIEW_BOXES`
          // for the pattern).
          <Stack
            sx={(muiTheme) => ({
              '& svg': {
                width: ['120px', '120px', 'auto'],
                height: [muiTheme.semantic.size['6xs'], muiTheme.semantic.size['6xs'], 'auto'],
              },
            })}
          >
            {logo}
          </Stack>
        ) : (
          logo
        )}
        <Stack
          // `display: 'none'` (not `visibility: 'hidden'`) below `md`: this removes the block
          // from the flex layout entirely, the same way unmounting it would, so the hero's
          // height below `md` is exactly the logo plus its padding with no phantom gap for a
          // hidden-but-still-laid-out block. This is a CSS breakpoint (a real `@media` rule
          // evaluated against the actual viewport), not a JS conditional: the title and
          // description are still in the DOM at every breakpoint, matching how `direction`,
          // `flex` and `paddingY` above already behave, and avoiding the SSR-then-hydrate flash
          // a `useMediaQuery`-driven unmount would cause on first paint.
          display={['none', 'none', 'flex']}
          maxWidth={theme.semantic.size.xs}
          gap={theme.semantic.spacing.xs}
          sx={{ textAlign: 'center' }}
        >
          <Typography variant="h5">{title}</Typography>
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            {description}
          </Typography>
        </Stack>
      </Stack>
      <Stack
        flex={[3, 1]}
        marginTop={[
          theme.semantic.spacing['2xl'],
          theme.semantic.spacing['2xl'],
          theme.semantic.spacing['6xl'],
        ]}
        alignItems="center"
      >
        {children}
      </Stack>
    </Stack>
  );
}
